import { reportBackendReachable, reportBackendUnreachable } from './connectivity';
import { loadLang } from '../i18n/loadLang';

export const BACKEND_URL: string = import.meta.env.VITE_BACKEND_URL ?? '';
export const API_BASE = `${BACKEND_URL}/api`;

let csrfRefreshPromise: Promise<void> | null = null;

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const doFetch = async () => {
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Accept-Language': getCurrentLang(),
          'X-XSRF-TOKEN': getCsrfToken(),
          ...options.headers,
        },
      });
      // Any HTTP response means the backend was reached.
      reportBackendReachable();
      return res;
    } catch (error) {
      // Network-level failure — feed the connectivity store so the offline
      // UI reacts even when navigator.onLine misreports (VIVATISPDB-435).
      if (error instanceof TypeError) {
        reportBackendUnreachable();
      }
      throw error;
    }
  };

  let res = await doFetch();

  if (res.status === 401) {
    if (!csrfRefreshPromise) {
      csrfRefreshPromise = fetchCsrfCookie().catch(() => {}).finally(() => {
        csrfRefreshPromise = null;
      });
    }
    await csrfRefreshPromise;
    res = await doFetch();
  }

  if (res.status === 401) {
    if (navigator.onLine && window.location.pathname !== '/login') {
      localStorage.removeItem('vivatis-logged-in');
      localStorage.removeItem('vivatis-auth');
      window.location.href = '/login';
    }
    throw new ApiError(401, 'Unauthenticated');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.message ?? 'Request failed', body);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: Record<string, unknown>,
  ) {
    super(message);
  }
}

export function getCsrfToken(): string {
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

/**
 * The language the UI renders in, so the backend answers (and writes mails)
 * in the same one via the Accept-Language header. Same rules as the
 * I18nProvider: the stored choice, else the browser language — a plain 'en'
 * fallback left mails half English while the UI was German (VIVATISPDB-536).
 */
export function getCurrentLang(): string {
  return loadLang();
}

export async function fetchCsrfCookie(): Promise<void> {
  await fetch(`${BACKEND_URL}/sanctum/csrf-cookie`, { credentials: 'include' });
}

export async function changePassword(payload: {
  current_password: string;
  password: string;
  password_confirmation: string;
}): Promise<void> {
  await api('/user/password', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}
